<template>
  <el-dialog
    :visible="visible"
    :before-close="handleClose"
    :title="title"
    :width="width"
    class="product-part-info-dialog"
  >
    <div slot="title" class="materialDetailTitle">
      <div class="materialDetailTitle_title">{{ title }}</div>
      <el-button
        type="primary"
        size="mini"
        icon="el-icon-download"
        class="export-button"
        @click="handleExport"
      >
        导出
      </el-button>
    </div>
    <el-tabs v-model="activeTab" class="product-part-tabs">
      <!-- 部件列表 -->
      <el-tab-pane label="部件列表" name="parts">
        <cardTable
          tableID="partInfoTable"
          :showPaging="false"
          :tableColumn="partsListColumn"
          :tableData="displayPartsList"
          :mobileColumns="mobileProps(partsListColumn)"
        />
      </el-tab-pane>
      <!-- 自检确认列表 -->
      <el-tab-pane label="自检确认列表" name="inspection">
        <cardTable
          tableID="inspectionTable"
          :showPaging="false"
          :tableColumn="inspectionListColumn"
          :tableData="displayInspectionList"
          :mobileColumns="mobileProps(inspectionListColumn)"
        />
      </el-tab-pane>
      <!-- 自检记录列表 -->
      <el-tab-pane label="自检记录列表" name="resInspection">
        <cardTable
          tableID="resInspectionTable"
          :showPaging="false"
          :tableColumn="resInspectionListColumn"
          :tableData="displayResInspectionList"
          :mobileColumns="mobileProps(resInspectionListColumn)"
        />
      </el-tab-pane>
      <!-- 问题列表 -->
      <el-tab-pane label="问题列表" name="issue">
        <cardTable
          tableID="issueTable"
          :showPaging="false"
          :tableColumn="issueListColumn"
          :tableData="displayIssueList"
          :mobileColumns="mobileProps(issueListColumn)"
        />
      </el-tab-pane>
    </el-tabs>
  </el-dialog>
</template>

<script>
import CardTable from './card-table.vue';
import FileSaver from 'file-saver';
import XLSX from 'xlsx';

/**
 * 产品部件信息弹窗（四表格切换）
 * 展示产品部件列表、自检确认列表、自检记录列表、问题列表，通过 Tab 切换。
 * 数据来源于 getProductInfoList（Type: 'ProductInformation'）返回的
 * parts / inspectionList / resInspectionList / issue 四个数组。
 */
export default {
  name: 'ProductPartInfoDialog',
  components: { CardTable },
  props: {
    // 是否显示弹窗
    visible: {
      type: Boolean,
      default: false
    },
    // 弹窗标题栏显示的完整标题（如："产品部件列表 - 产品编号: xxx"）
    title: {
      type: String,
      default: ''
    },
    // 产品编号（用于导出文件名，如 "10508262176详情.xlsx"）
    productCode: {
      type: String,
      default: ''
    },
    // 弹窗宽度
    width: {
      type: String,
      default: '80%'
    },
    // 部件列表
    partsList: {
      type: Array,
      default: () => []
    },
    // 自检确认列表
    inspectionList: {
      type: Array,
      default: () => []
    },
    // 自检记录列表
    resInspectionList: {
      type: Array,
      default: () => []
    },
    // 问题列表
    issueList: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      // 当前激活的 Tab
      activeTab: 'parts',
      // 部件列表列定义
      partsListColumn: [
        { prop: 'serialCode', label: '部件编号' },
        { prop: 'fsn', label: '短代码' },
        { prop: 'name', label: '名称' },
        { prop: 'model', label: '型号' },
        { prop: 'displayTime', label: '时间' }
      ],
      // 自检确认列表列定义
      inspectionListColumn: [
        { prop: 'content', label: '内容' },
        { prop: 'indicator', label: '指标要求' },
        { prop: 'confirmValue', label: '确认' },
        { prop: 'recorder', label: '操作者' },
        { prop: 'displayTime', label: '日期' }
      ],
      // 自检记录列表列定义
      resInspectionListColumn: [
        { prop: 'id', label: 'ID' },
        { prop: 'content', label: '组分' },
        { prop: 'resultZero', label: '零点' },
        { prop: 'resultData', label: '量程' },
        { prop: 'note', label: '备注' },
        { prop: 'productId', label: '关联产品ID' },
        { prop: 'recorder', label: '记录人' },
        { prop: 'displayTime', label: '记录时间' }
      ],
      // 问题列表列定义
      issueListColumn: [
        { prop: 'create_time', label: '创建时间' },
        { prop: 'operation_person_name', label: '人员' },
        { prop: 'SHOWVALUE', label: '类型' },
        { prop: 'field0002', label: '问题' },
        { prop: 'detail', label: '备注' }
      ]
    };
  },
  computed: {
    /** 部件列表：补充显示用时间字段 */
    displayPartsList() {
      return (this.partsList || []).map(item => ({
        ...item,
        displayTime: this.formatDate(item.updateTime || item.createTime)
      }));
    },
    /** 自检确认列表：补充日期字段与确认内容
     * 确认列规则：B_result 为 true 时取 result 内容；否则 B_resultData 为 true 时取 resultData 内容；
     * 对应字段未传（空）时确认列为空不显示。
     */
    displayInspectionList() {
      return (this.inspectionList || []).map(item => {
        let confirmValue = '';
        if (item.B_result && this.hasValue(item.result)) {
          confirmValue = item.result;
        } else if (item.B_resultData && this.hasValue(item.resultData)) {
          confirmValue = item.resultData;
        }
        return {
          ...item,
          displayTime: this.formatDate(item.createDate),
          confirmValue
        };
      });
    },
    /** 自检记录列表：补充显示用日期字段 */
    displayResInspectionList() {
      return (this.resInspectionList || []).map(item => ({
        ...item,
        displayTime: this.formatDate(item.createDate)
      }));
    },
    /** 问题列表：补充序号（从 1 开始） */
    displayIssueList() {
      return (this.issueList || []).map((item, index) => ({
        ...item,
        index: index + 1
      }));
    }
  },
  methods: {
    /** ✅ 手机端要显示的列（prop 列表）
     *  card-table 的规则：手机模式下如果没传 mobileColumns，
     *  就只渲染列定义里 showOnMobile === true 的列 → 不传的话大部分列会消失。
     *  这里把当前 Tab 的全部列都传进去，保证手机 / 电脑看到的信息列完全一致。 */
    mobileProps(cols) {
      return (cols || []).map(col => col.prop);
    },
    /** 日期格式化：取前 10 位（yyyy-MM-dd） */
    formatDate(val) {
      if (!val) return '';
      const str = String(val);
      return str.length >= 10 ? str.substring(0, 10) : str;
    },
    /** 字段是否有值（未传 / null / 空字符串均视为无值） */
    hasValue(val) {
      return val !== undefined && val !== null && val !== '';
    },
    /** 关闭弹窗（由父组件控制 visible） */
    handleClose() {
      this.$emit('close');
    },

    /**
     * 导出四个列表为 Excel（多 Sheet），文件名：产品编号详情.xlsx
     * 与 productProgress 的导出方式保持一致：XLSX 通过 getElementsByTagName('tr')
     * 收集 el-table 容器内的所有表头/数据行，因此直接对 tableID 对应的元素导出即可。
     */
    handleExport() {
      const xlsxParam = { raw: true };
      const wb = XLSX.utils.book_new();
      try {
        const tables = [
          { id: 'partInfoTable', name: '部件列表' },
          { id: 'inspectionTable', name: '自检确认列表' },
          { id: 'resInspectionTable', name: '自检记录列表' },
          { id: 'issueTable', name: '问题列表' }
        ];
        tables.forEach(({ id, name }) => {
          const el = document.getElementById(id);
          if (el) {
            const ws = XLSX.utils.table_to_sheet(el, xlsxParam);
            XLSX.utils.book_append_sheet(wb, ws, name);
          }
        });
        const wbout = XLSX.write(wb, {
          bookType: 'xlsx',
          bookSST: true,
          type: 'array'
        });
        const fileName = (this.productCode) + '产品详情.xlsx';
        FileSaver.saveAs(
          new Blob([wbout], { type: 'application/octet-stream' }),
          fileName
        );
      } catch (e) {
        // 导出失败时明确提示，便于定位问题
        this.$message.error('导出失败：' + (e && e.message ? e.message : e));
      }
    }
  }
};
</script>

<style scoped lang="scss">
/* ✅ 表格圆角：全站 CardInfoTable 统一用它，想改大小只动这一个变量 */
$table-radius: 6px;

/* ✅ 弹窗本体圆角：比表格略大一圈（外层 8px → 内层 6px），想改只动这一个变量 */
$dialog-radius: 8px;

.materialDetailTitle {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
  /* 为右上角 el-dialog 自带的关闭按钮留出空间，避免导出按钮与其重叠 */
  padding-right: 60px;

  .materialDetailTitle_title {
    font-size: 14px;   /* 原 16px → 标题小一点 */
    font-weight: 500;
  }

  /* ✅ 导出按钮：比 size="mini" 再小一号 */
  .export-button {
    padding: 3px 8px !important;
    font-size: 12px !important;
    height: 24px !important;
    line-height: 18px !important;
    border-radius: 3px;
  }
}

/* 手机端：标题和导出按钮再收一档（不影响弹窗尺寸 / 布局） */
@media screen and (max-width: 768px) {
  .materialDetailTitle {
    padding-right: 28px;

    .materialDetailTitle_title {
      font-size: 12px;
    }

    .export-button {
      padding: 2px 2px !important;
      font-size: 11px !important;
      height: 22px !important;
      line-height: 16px !important;
    }
  }
}

.product-part-tabs {
  width: 100%;
  height: 60vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  /* Tab 切换栏固定，不随表格内容滚动 */
  ::v-deep .el-tabs__header {
    flex-shrink: 0;
    margin-bottom: 10px;
  }

  /* 表格内容区占满剩余高度，滚动收敛到表格内部 */
  ::v-deep .el-tabs__content {
    flex: 1;
    min-height: 0;
    overflow: hidden;
    /* ✅ 内容区圆角：overflow:hidden 把里面的表格四角一起裁圆
       （tab 头 .el-tabs__header 保持直角，只有下面的表格区域是圆的） */
    border-radius: $table-radius;
  }

  ::v-deep .el-tab-pane {
    height: 100%;
    border-radius: $table-radius;
    overflow: hidden;
  }

  /* ✅ 表格本体圆角：和外层同口径（$table-radius）
     每个 tab 页里都是各自的 card-table，这里统一保证四个 tab 口径一致。
     表头只圆上面两角、表体只圆下面两角，拼起来才是完整的一圈。 */
  ::v-deep .el-table {
    border-radius: $table-radius;
    overflow: hidden;
  }
  ::v-deep .el-table__header-wrapper,
  ::v-deep .el-table__fixed-header-wrapper {
    border-radius: $table-radius $table-radius 0 0;
    overflow: hidden;
  }
  ::v-deep .el-table__body-wrapper,
  ::v-deep .el-table__fixed-body-wrapper,
  ::v-deep .el-table__empty-block {
    border-radius: 0 0 $table-radius $table-radius;
  }
}

/* ✅ 弹窗本体圆角（.el-dialog）
   ⚠️ class 写在 <el-dialog> 上会落到根元素 .el-dialog__wrapper，真正的弹窗盒子
      .el-dialog 是它的子元素 —— 所以必须用 ::v-deep 才能命中
      （同 StationInfoGas 里 .parts-dialog-dark ::v-deep .el-dialog 的写法） */
.product-part-info-dialog ::v-deep .el-dialog {
  border-radius: $dialog-radius;
  overflow: hidden;
}

/* 弹窗内容区：不再整体滚动，纵向滚动只发生在表格内部 */
::v-deep .el-dialog__body {
  overflow: hidden;
  padding: 0px 20px 20px; /* 顶部 padding 收窄为 10px，左右保持 20px */
}

/* ✅ 手机端：Tab 内容（el-tab-pane）字体 10px、右侧间距 5px */
@media screen and (max-width: 768px) {
  .product-part-tabs {
    /* Tab 标签本身也跟着缩小 */
    ::v-deep .el-tabs__item {
      font-size: 10px !important;
      padding: 0 6px !important;
    }

    /* el-tab-pane：字体 10px + padding-right 5px */
    ::v-deep .el-tab-pane {
      font-size: 10px !important;
      padding-right: 5px !important;
    }

    /* 表格表头 / 表体统一 10px，单元格右侧间距 5px */
    ::v-deep .el-table th,
    ::v-deep .el-table td {
      font-size: 10px !important;
      padding-right: 0px !important;
    }

    ::v-deep .el-table th .cell,
    ::v-deep .el-table td .cell {
      font-size: 10px !important;
      padding-right: 0px !important;
    }
  }
}
</style>
